<template>
  <div class="legal-video-container">
    <div class="video-header">
      <h3>{{ $t('kyc_legal_video_title') }}</h3>
      <p class="video-description">{{ $t('kyc_legal_video_desc') }}</p>
    </div>

    <!-- 驳回信息展示 -->
    <div v-if="model.legalVideoPathMessage" class="rejection-info">
      <a-alert
        type="warning"
        show-icon
        :message="$t('kyc_video_rejection_title')"
        :description="model.legalVideoPathMessage"
        class="rejection-alert"
      />
    </div>

    <!-- 视频播放区域 -->
    <div v-if="hasVideos" class="video-player-section" :class="`status-${videoSectionStatus}`">
      <h4>
        {{ $t('kyc_uploaded_videos') }}
        <span v-if="videoSectionStatus === 'rejected'" class="status-text rejected">
          - {{ $t('kyc_video_status_rejected') }}
        </span>
        <span v-if="videoSectionStatus === 'updated'" class="status-text updated">
          - {{ $t('kyc_video_status_updated') }}
        </span>
      </h4>

      <!-- 视频加载中提示 -->
      <div v-if="videosLoading" class="videos-loading">
        <a-spin :tip="$t('kyc_videos_loading')" />
      </div>

      <div v-else class="video-grid">
        <div
          v-for="(videoPath, index) in model.legalVideoPath"
          :key="index"
          class="video-item"
        >
          <video
            :src="getVideoUrlSync(videoPath)"
            controls
            preload="metadata"
            class="video-player"
            @error="handleVideoError(index)"
          >
            {{ $t('kyc_video_not_supported') }}
          </video>
          <p class="video-label">{{ $t('kyc_video_label', [index + 1]) }}</p>
        </div>
      </div>
    </div>

    <!-- 视频刷新按钮 -->
    <div class="video-refresh">
      <a-button
        type="text"
        size="small"
        class="video-refresh-btn"
        :loading="videosLoading"
        @click="checkNewUpload(model.legalVideoPath[0])"
      >
        <reload-outlined />
        {{ $t('kyc_refresh_video') }}
      </a-button>
    </div>

    <!-- 无视频或需要重新录制 -->
    <div v-if="!hasVideos || model.legalVideoPathMessage" class="video-collection-section">
      <div class="collection-info">
        <video-camera-outlined class="collection-icon" />
        <h4>{{ $t('kyc_video_collection_title') }}</h4>
        <p>{{ $t('kyc_video_collection_desc') }}</p>
      </div>

      <a-button
        type="primary"
        size="large"
        class="collection-btn"
        :loading="qrLoading"
        @click="showQRCode"
      >
        <qrcode-outlined />
        {{ $t('kyc_show_qr_code') }}
      </a-button>
    </div>

    <!-- 二维码弹窗 -->
    <a-modal
      v-model:visible="qrVisible"
      :title="$t('kyc_video_qr_title')"
      :footer="null"
      width="480px"
      centered
      class="qr-modal"
    >
      <div class="qr-content">
        <div class="qr-code-container">
          <qr-code
            v-if="videoCollectionUrl"
            :text="videoCollectionUrl"
            :size="240"
            :margin="10"
          />
          <a-spin v-else :tip="$t('kyc_generating_qr')" />
        </div>

        <!-- 刷新二维码按钮 -->
        <div class="qr-refresh">
          <a-button
            type="text"
            size="small"
            class="refresh-btn"
            :loading="qrLoading"
            :disabled="!videoCollectionUrl"
            @click="refreshQRCode"
          >
            <reload-outlined />
            {{ $t('kyc_refresh_qr') }}
          </a-button>
        </div>

        <div class="qr-instructions">
          <h4>{{ $t('kyc_qr_instructions_title') }}</h4>
          <ul class="instruction-list">
            <li>{{ $t('kyc_qr_instruction_1') }}</li>
            <li>{{ $t('kyc_qr_instruction_2') }}</li>
            <li>{{ $t('kyc_qr_instruction_3') }}</li>
          </ul>
        </div>

        <div class="qr-actions">
          <a-button :disabled="!videoCollectionUrl" @click="copyUrl">
            <copy-outlined />
            {{ $t('kyc_copy_link') }}
          </a-button>
          <a-button :disabled="!videoCollectionUrl" @click="downloadQR">
            <download-outlined />
            {{ $t('kyc_download_qr') }}
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 视频录制状态提示 -->
    <div v-if="hasVideos && (!model.legalVideoPathMessage || state.newVideoUploaded)" class="video-status">
      <a-alert
        type="success"
        show-icon
        :message="$t('kyc_video_uploaded_success')"
        :description="$t('kyc_video_processing_desc')"
      />
    </div>

    <div style="text-align: right; margin-top: 30px">
      <a-button
        type="primary"
        shape="round"
        ghost
        style="min-width: 138px; margin-right: 24px"
        @click="$emit('next', '2-5')"
      >{{ $t('pages_merchant_040') }}</a-button>
      <a-button
        html-type="submit"
        type="primary"
        shape="round"
        style="min-width: 138px"
        @click="$emit('next', '3-2')"
      >{{ $t('pages_merchant_020') }}</a-button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, toRefs } from 'vue'
import { message } from 'ant-design-vue'
import { useStore } from 'vuex'
import {
  VideoCameraOutlined,
  QrcodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue'
import QrCode from '@/components/qr-code'
import { _user, _oss } from '@/api'
import i18n from '@/locale'

export default {
  name: 'LegalVideo',
  components: {
    QrCode,
    VideoCameraOutlined,
    QrcodeOutlined,
    CopyOutlined,
    DownloadOutlined,
    ReloadOutlined
  },
  props: {
    model: {
      type: Object,
      default: () => { return {} }
    },
    refused: {
      type: Boolean,
      default: () => { return false }
    }
    // legalVideoPath: {
    //   type: Array,
    //   default: () => []
    // },
    // legalVideoPathMessage: {
    //   type: String,
    //   default: ''
    // }
  },
  setup(props, { emit }) {
    const qrVisible = ref(false)
    const qrLoading = ref(false)
    const videoCollectionUrl = ref('')
    const videoUrls = ref({}) // 存储视频URL的映射
    const videosLoading = ref(false) // 视频加载状态
    const store = useStore()

    const state = reactive({
      userInfo: store.getters.getUserInfo,
      newVideoUploaded: false,
      hasCheckedNewUpload: false // 是否已执行过checkNewUpload
    })

    state.modelForm = computed(() => {
      return props.model
    })

    // 计算属性
    const hasVideos = computed(() => {
      return state.modelForm.legalVideoPath && state.modelForm.legalVideoPath.length > 0
    })

    // 计算视频区域的状态
    const videoSectionStatus = computed(() => {
      if (state.modelForm.legalVideoPathMessage) {
        // 有驳回信息
        if (!state.hasCheckedNewUpload) {
          return 'rejected' // 红框状态
        } else if (state.newVideoUploaded) {
          return 'updated' // 绿框状态
        } else {
          return 'rejected' // 仍然是红框状态
        }
      }
      return 'normal' // 正常状态
    })

    // 获取完整视频URL
    const getVideoUrl = async (relativePath) => {
      if (!relativePath) return ''

      // 如果已经缓存了URL，直接返回
      if (videoUrls.value[relativePath]) {
        return videoUrls.value[relativePath]
      }

      try {
        const path = await _oss.getOssFileUrl([relativePath])
        if (path.data.succ) {
          const url = Object.values(path.data.data)[0]
          console.log('video url', url)
          // 缓存URL
          videoUrls.value[relativePath] = url
          return url
        }
      } catch (error) {
        console.error('获取视频URL失败:', error)
      }
      return ''
    }

    // 预加载所有视频URL
    const preloadVideoUrls = async () => {
      if (state.modelForm.legalVideoPath && state.modelForm.legalVideoPath.length > 0) {
        videosLoading.value = true
        try {
          for (const videoPath of state.modelForm.legalVideoPath) {
            await getVideoUrl(videoPath)
          }
        } finally {
          videosLoading.value = false
        }
      }
    }

    // 获取视频URL的同步版本（用于模板）
    const getVideoUrlSync = (relativePath) => {
      console.log('getVideoUrlSync', relativePath, videoUrls.value[relativePath])
      return videoUrls.value[relativePath] || ''
    }

    // 处理视频加载错误
    const handleVideoError = (index) => {
      // message.error(i18n.global.t('kyc_video_load_error', [index + 1]))
    }

    // 生成视频采集链接
    const generateCollectionUrl = async () => {
      try {
        qrLoading.value = true
        const res = await _user.refreshVideoRecordToken()

        if (res.data.succ) {
          const url = 'https://kyc.ezways.net/?token=' + res.data.data + '&env=' + process.env.NODE_ENV + '&id=' + state.userInfo.id + '&merchantNo=' + state.userInfo.merchantNo
          videoCollectionUrl.value = url
          return url
        } else {
          message.error(res.data.resMsg || i18n.global.t('kyc_generate_url_failed'))
          return null
        }
      } catch (error) {
        console.error('生成视频采集链接失败:', error)
        message.error(i18n.global.t('kyc_generate_url_failed'))
        return null
      } finally {
        qrLoading.value = false
      }
    }

    // 显示二维码
    const showQRCode = async () => {
      qrVisible.value = true

      if (!videoCollectionUrl.value) {
        await generateCollectionUrl()
      }
    }

    // 复制链接
    const copyUrl = async () => {
      if (!videoCollectionUrl.value) return

      try {
        await navigator.clipboard.writeText(videoCollectionUrl.value)
        message.success(i18n.global.t('kyc_copy_success'))
      } catch (error) {
        // 降级处理
        const textarea = document.createElement('textarea')
        textarea.value = videoCollectionUrl.value
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        message.success(i18n.global.t('kyc_copy_success'))
      }
    }

    // 下载二维码
    const downloadQR = () => {
      if (!videoCollectionUrl.value) return

      try {
        const canvas = document.querySelector('.qr-code-container canvas')
        if (canvas) {
          const link = document.createElement('a')
          link.download = 'legal-video-collection-qr.png'
          link.href = canvas.toDataURL()
          link.click()
          message.success(i18n.global.t('kyc_download_success'))
        }
      } catch (error) {
        message.error(i18n.global.t('kyc_download_failed'))
      }
    }

    // 刷新二维码
    const refreshQRCode = async () => {
      try {
        videoCollectionUrl.value = ''
        await generateCollectionUrl()
        message.success(i18n.global.t('kyc_refresh_qr_success'))
      } catch (error) {
        message.error(i18n.global.t('kyc_refresh_qr_failed'))
      }
    }

    // 刷新单个视频URL
    const refreshVideoUrl = async (videoPath) => {
      try {
        // 清除缓存的URL
        delete videoUrls.value[videoPath]
        // 重新获取URL
        await getVideoUrl(videoPath)
        message.success(i18n.global.t('kyc_refresh_video_success'))
      } catch (error) {
        message.error(i18n.global.t('kyc_refresh_video_failed'))
      }
    }

    const checkNewUpload = async (videoPath) => {
      // 标记已执行过checkNewUpload
      state.hasCheckedNewUpload = true

      const res = await _user.getKycVideo()
      if (res.data.succ) {
        const { data } = res.data
        console.log('checkNewUpload', data, videoPath)
        // compare with videoPath
        if (data && data !== videoPath) {
          console.log('New video detected:', data)
          // 清除旧视频的缓存URL
          delete videoUrls.value[videoPath]

          // new video was uploaded, update
          state.modelForm.legalVideoPath = [data]
          state.modelForm.rejectUpdated = true
          // state.modelForm = {
          //   ...state.modelForm,
          //   legalVideoPath: [data],
          //   rejectUpdated: true
          // }

          state.newVideoUploaded = true
          console.log('New video uploaded, state updated:', state.newVideoUploaded)
          message.success(i18n.global.t('kyc_refresh_video_success'))

          // 获取新视频的URL
          const newUrl = await getVideoUrl(data)
          // 强制刷新视频播放
          if (newUrl) {
            setTimeout(() => {
              const videoElements = document.querySelectorAll('.video-player')
              videoElements.forEach(video => {
                // 更新video元素的src并重新加载
                video.src = newUrl
                video.load()
                console.log('Force reload video with new URL:', newUrl)
              })
            }, 100)
          }
        } else {
          message.info(i18n.global.t('kyc_no_new_video'))
        }
      }
    }

    // 组件挂载时检查视频状态
    onMounted(async () => {
      // 预加载视频URL
      await checkNewUpload(state.modelForm.legalVideoPath && state.modelForm.legalVideoPath[0])
      await preloadVideoUrls()

      // 如果有驳回信息，发送事件通知父组件
      // if (props.model.legalVideoPathMessage) {
      //   emit('video-rejected', props.model.legalVideoPathMessage)
      // }

      // // 如果有视频，发送事件通知父组件
      // if (hasVideos.value) {
      //   emit('video-uploaded', props.model.legalVideoPath)
      // }
    })

    // 监听props变化，重新加载视频URL
    watch(() => state.modelForm.legalVideoPath, async (newPaths) => {
      if (newPaths && newPaths.length > 0) {
        await preloadVideoUrls()
      }
    }, { deep: true })

    // 监听驳回信息变化，重置检查状态
    watch(() => state.modelForm.legalVideoPathMessage, (newMessage) => {
      if (newMessage) {
        // 有新的驳回信息时，重置状态
        state.hasCheckedNewUpload = false
        state.newVideoUploaded = false
      }
    })

    return {
      // ...toRefs(props),
      state,
      qrVisible,
      qrLoading,
      videoCollectionUrl,
      videosLoading,
      hasVideos,
      videoSectionStatus,
      getVideoUrl,
      getVideoUrlSync,
      handleVideoError,
      showQRCode,
      copyUrl,
      downloadQR,
      refreshQRCode,
      refreshVideoUrl,
      checkNewUpload,
      ...toRefs(state)
    }
  }
}
</script>

<style lang="less" scoped>
.legal-video-container {
  padding: 24px;
  background: #fff;
  border-radius: 8px;

  .video-header {
    margin-bottom: 24px;
    text-align: center;

    h3 {
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: 600;
      color: #262626;
    }

    .video-description {
      color: #8c8c8c;
      margin: 0;
    }
  }

  .rejection-info {
    margin-bottom: 24px;

    .rejection-alert {
      border-radius: 6px;
    }
  }

  .video-player-section {
    margin-bottom: 32px;
    padding: 16px;
    border-radius: 8px;
    transition: border-color 0.3s ease;

    h4 {
      margin-bottom: 16px;
      font-size: 16px;
      font-weight: 600;
      color: #262626;

      .status-text {
        font-size: 14px;
        font-weight: 500;

        &.rejected {
          color: #ff4d4f;
        }

        &.updated {
          color: #52c41a;
        }
      }
    }

    // 不同状态的边框样式
    &.status-rejected {
      border: 2px solid #ff4d4f;
      background: rgba(255, 77, 79, 0.05);
    }

    &.status-updated {
      border: 2px solid #52c41a;
      background: rgba(82, 196, 26, 0.05);
    }

    &.status-normal {
      border: 2px solid transparent;
    }

    .videos-loading {
      text-align: center;
      padding: 40px;
      color: #666;
    }

    .video-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 20px;

      .video-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        .video-player {
          width: 100%;
          max-width: 400px;
          height: 225px;
          background: #f5f5f5;
          border-radius: 8px;
          object-fit: cover;
        }

        .video-label {
          margin-top: 8px;
          text-align: center;
          font-size: 14px;
          color: #595959;
        }
      }
    }
  }

  // 外层视频刷新按钮样式
  .video-refresh {
    text-align: center;
    margin: 16px 0 24px 0;

    .video-refresh-btn {
      color: #1890ff;
      font-size: 14px;
      padding: 6px 16px;
      height: auto;
      border-radius: 6px;

      &:hover {
        color: #40a9ff;
        background: rgba(24, 144, 255, 0.08);
      }

      &:disabled {
        color: #d9d9d9;
        background: transparent;
      }

      .anticon {
        margin-right: 6px;
      }
    }
  }

  .video-collection-section {
    text-align: center;
    padding: 48px 24px;

    .collection-info {
      margin-bottom: 32px;

      .collection-icon {
        font-size: 48px;
        color: @main-color;
        margin-bottom: 16px;
      }

      h4 {
        margin-bottom: 8px;
        font-size: 18px;
        font-weight: 600;
        color: #262626;
      }

      p {
        color: #8c8c8c;
        margin: 0;
        max-width: 400px;
        margin: 0 auto;
      }
    }

    .collection-btn {
      height: 48px;
      padding: 0 32px;
      font-size: 16px;
      border-radius: 24px;
    }
  }

  .video-status {
    margin-top: 24px;
  }
}

.qr-modal {
  .qr-content {
    text-align: center;

    .qr-code-container {
      padding: 20px;
      background: #fafafa;
      border-radius: 8px;
      margin-bottom: 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 280px;
    }

    .qr-refresh {
      text-align: center;
      margin-bottom: 24px;

      .refresh-btn {
        color: #1890ff;
        font-size: 12px;
        padding: 4px 8px;
        height: auto;

        &:hover {
          color: #40a9ff;
          background: rgba(24, 144, 255, 0.06);
        }

        &:disabled {
          color: #d9d9d9;
        }
      }
    }

    .qr-instructions {
      text-align: left;
      margin-bottom: 24px;

      h4 {
        margin-bottom: 12px;
        font-size: 14px;
        font-weight: 600;
        color: #262626;
      }

      .instruction-list {
        margin: 0;
        padding-left: 20px;
        color: #595959;

        li {
          margin-bottom: 8px;
          line-height: 1.5;
        }
      }
    }

    .qr-actions {
      display: flex;
      gap: 12px;
      justify-content: center;

      .ant-btn {
        flex: 1;
        height: 40px;
      }
    }
  }
}
</style>
